import { ContestType } from '@betterpool/api-types/contests-service';
import { SlateStatus } from '@betterpool/api-types/games-service';
import { format, isValid } from 'date-fns';

import type { ContestSlate } from '~/domains/games/interfaces/contest.slates.interface';

const parseDate = (dateValue: string | number | Date): Date | null => {
  if (!dateValue) return null;

  if (dateValue instanceof Date) {
    return isValid(dateValue) ? dateValue : null;
  }

  if (typeof dateValue === 'string' || typeof dateValue === 'number') {
    const parsedDate = new Date(dateValue);
    return isValid(parsedDate) ? parsedDate : null;
  }

  return null;
};

const getFirstActiveSlate = (slates: ContestSlate[]) =>
  slates?.find(
    (slate) =>
      slate.status === SlateStatus.IN_PROGRESS ||
      slate.status === SlateStatus.LIVE ||
      slate.status === SlateStatus.SCHEDULED
  );

export const isKnockoutStagePickDeadline = ({
  contestType,
  groupStageSlateId,
}: {
  contestType: ContestType;
  groupStageSlateId?: string;
}) => contestType === ContestType.TEAM_SURVIVOR && !!groupStageSlateId;

const getPickDeadlineValue = ({
  contestType,
  pickDeadlineType,
  slateLabel,
  slates,
}: {
  contestType: ContestType;
  slateLabel: string;
  pickDeadlineType: string;
  slates: ContestSlate[];
}) => {
  if (contestType === ContestType.TEAM_SURVIVOR || contestType === ContestType.TEAM_PICK_EM) {
    if (pickDeadlineType === 'game') {
      return 'Game start';
    }
    const firstActiveSlate = getFirstActiveSlate(slates);
    if (!firstActiveSlate) return null;

    const picksLockAt = parseDate(firstActiveSlate.picksLockAt);
    if (picksLockAt) {
      return format(picksLockAt, "ccc, MMM d 'at' h:mm a");
    }

    const startDate = parseDate(firstActiveSlate.start_date);
    if (startDate) {
      return format(startDate, "ccc, MMM d 'at' h:mm a");
    }

    return `${firstActiveSlate.name} start`;
  }

  if (
    contestType === ContestType.PLAYER_TIER ||
    contestType === ContestType.PLAYER_ONE_AND_DONE ||
    contestType === ContestType.PLAYER_SURVIVOR
  ) {
    if (pickDeadlineType === 'game') {
      return null;
    }

    return `${slateLabel} start`;
  }

  return null;
};

export default getPickDeadlineValue;
